/**
 * SD-WAN 정책 store
 *
 * @since 2022-11-23
 * @author wj_tams_dev01
 */

// utils
import {
  CmApiPrefix as poApiPrefix
} from 'utils'

import {
  pickAppData
} from '~/utils/policyUtils'

// SD-WAN 정책 주소 데이터 초기화
const initSdwanAddressData = () => {
  return [{
    addr_obj_id: 0,
    addr_obj_type: 0,
    addr_obj_ip_ver_type: 0,
    addr_obj_zone_type: 0,
    addr_obj_uname: 'Any',
    addr_obj_desc: '',
    addr_obj_except_yn: 0, // 0: No, 1: Yes
    addr_obj_ip_exp: ''
  }]
}

// SD-WAN 정책 애플리케이션 데이터 초기화
const initSdwanApplicationData = () => {
  return [{
    app_obj_id: 0,
    app_obj_name: 'Any',
    app_obj_use_yn: 0,
    app_type: 0, // 1:App Object, 2: App Group Object, 3: App Filter
    app_obj_port: ''
  }]
}

// SD-WAN 정책 - 서비스 객체 데이터
const initSdwanServiceData = () => {
  return [{
    srv_obj_id: 0,
    srv_obj_type: 0,
    srv_obj_pre_def_yn: 0,
    srv_obj_uname: 'Any',
    srv_obj_desc: '',
    srv_obj_cate_type: 0,
    srv_obj_life_time_num: 0,
    srv_obj_prio_num: 0,
    srv_obj_src_port_exp: '',
    srv_obj_dst_port_exp: '',
    prtc_obj: {
      prtc_obj_id: 0,
      prtc_obj_prct: 0,
      prtc_obj_pre_def_yn: 0,
      prtc_obj_uname: '',
      prtc_obj_desc: ''
    }
  }]
}

// SD-WAN 정책 경로 레이블 데이터 초기화
const initSdwanPathLabelData = () => {
  return []
}

// SD-WAN 정책 애플리케이션 필터 데이터 초기화
const initSdwanAppFilterData = () => {
  return [{
    app_filter_obj_id: 0,
    app_type: 0,
    app_filter_obj_desc: '',
    app_filter_obj_uname: '',
    app_cate: [{
      app_cate_id: 0,
      app_cate_uname: ''
    }],
    app_char: [{
      app_char_id: 0,
      app_char_uname: ''
    }],
    app_cntry: [{
      app_cntry_id: 0,
      app_cntry_code: 0,
      app_cntry_uname: ''
    }],
    app_risk: [{
      app_risk_id: 0,
      app_risk_uname: ''
    }],
    app_tag: [{
      app_tag_id: 0,
      app_tag_uname: ''
    }],
    app_tech: [{
      app_tech_id: 0,
      app_tech_uname: ''
    }]
  }]
}

const isExistPolicy = (policyList, targetPolicy) => {
  return _.findIndex(policyList, policy => {
    return policy.sdwan_policy_id === targetPolicy.sdwan_policy_id
  }) >= 0
}

// SD-WAN 정책 전체 데이터 초기화
const initSdwanPolicyAll = () => {
  return {
    src_addr_obj: initSdwanAddressData(),
    dst_addr_obj: initSdwanAddressData(),
    srv_obj: initSdwanServiceData(),
    app_obj: initSdwanApplicationData(),
    app_filter_obj: initSdwanAppFilterData(),
    sdwan_path_label_obj: initSdwanPathLabelData(),
    sdwan_policy_id: 0,
    sdwan_policy_name: '',
    sdwan_policy_load_balance: 0, // 0: Priority, 1: Session
    sdwan_policy_fail_action: 0, // 0: Drop, 1: Bypass
    sdwan_policy_log_yn: 1, // 0: Off, 1: On 디폴트값 1로 항상 요청 ui에서는 표현 안함
    sdwan_policy_desc: ''
  }
}

const genPolicyObj = ({isAddProfilePolicyFlag, policy}) => {
  const dummyObj = initSdwanPolicyAll()
  if (isAddProfilePolicyFlag) dummyObj.sdwan_policy_select_yn = 0
  dummyObj.sdwan_policy_id = policy.sdwan_policy_id
  dummyObj.sdwan_policy_name = policy.sdwan_policy_name
  dummyObj.sdwan_policy_load_balance = policy.sdwan_policy_load_balance
  dummyObj.sdwan_policy_fail_action = policy.sdwan_policy_fail_action
  dummyObj.sdwan_policy_log_yn = policy.sdwan_policy_log_yn
  dummyObj.sdwan_policy_desc = policy.sdwan_policy_desc
  let isEmptyAppObj = false
  _.forEach(['app_obj', 'src_addr_obj', 'dst_addr_obj', 'srv_obj', 'sdwan_path_label_obj'], key => {
    const objArr = _.get(policy, [key], [])
    if (_.isArray(objArr) && objArr.length > 0) dummyObj[key] = objArr
    else if (key === 'app_obj') {
      dummyObj.app_obj = initSdwanApplicationData()
      isEmptyAppObj = true
    } else if (key === 'sdwan_path_label_obj') dummyObj[key] = []
    else if (key === 'srv_obj') dummyObj[key] = initSdwanServiceData()
    else dummyObj[key] = initSdwanAddressData()
  })
  if (_.isArray(policy.app_filter_obj) && policy.app_filter_obj.length > 0) {
    dummyObj.app_filter_obj = policy.app_filter_obj
    // SD-WAN 정책에서는 app_obj 와 app_filter_obj 정보를 합친다.
    let convertedAppFilterObj = null
    if (isEmptyAppObj) dummyObj.app_obj = []
    policy.app_filter_obj.forEach((appFilterObj) => {
      convertedAppFilterObj = {
        app_obj_id: appFilterObj.app_filter_obj_id,
        app_obj_name: appFilterObj.app_filter_obj_uname,
        app_type: 3, // App Filter
        app_filter_obj_desc: appFilterObj.app_filter_obj_desc,
        app_cate: (_.isUndefined(appFilterObj.app_cate) ? [] : appFilterObj.app_cate),
        app_tech: (_.isUndefined(appFilterObj.app_tech) ? [] : appFilterObj.app_tech),
        app_char: (_.isUndefined(appFilterObj.app_char) ? [] : appFilterObj.app_char),
        app_tag: (_.isUndefined(appFilterObj.app_tag) ? [] : appFilterObj.app_tag),
        app_cntry: (_.isUndefined(appFilterObj.app_cntry) ? [] : appFilterObj.app_cntry),
        app_risk: (_.isUndefined(appFilterObj.app_risk) ? [] : appFilterObj.app_risk)
      }
      dummyObj.app_obj.push(convertedAppFilterObj)
    })
  } else {
    dummyObj.app_filter_obj = initSdwanAppFilterData()
  }
  return dummyObj
}

const getDefaultState = () => {
  return {
    sdwanPolicyList: [],
    sdwanProfilePolicyList: [],
    sdwanPolicyData: initSdwanPolicyAll(),
    selectedAddObjectData: void 0,
    searchSelectedPolicyData: void 0
  }
}

export const state = () => getDefaultState()

export const mutations = {
  // SD-WAN 정책 목록 초기화
  init (state) {
    state.sdwanPolicyList = []
    state.sdwanProfilePolicyList = []
    state.sdwanPolicyData = initSdwanPolicyAll()
    state.selectedAddObjectData = void 0
    state.searchSelectedPolicyData = void 0
  },
  // SD-WAN 프로파일 정책 목록 초기화 (프로파일 기능에서 사용함)
  initProfilePolicyList (state) {
    state.sdwanProfilePolicyList = []
    state.sdwanPolicyData = initSdwanPolicyAll()
  },
  // SD-WAN 정책 목록 조회
  resetSdwanPolicyData (state) {
    state.sdwanPolicyData = initSdwanPolicyAll()
    state.selectedAddObjectData = void 0
    state.searchSelectedPolicyData = void 0
  },
  getSdwanProfileList (state, {item}) {
    const result = []
    if (_.isPlainObject(item)) item = [item]
    if (_.get(item, ['length']) === 0) {
      state.sdwanProfilePolicyList = (result.length > 0) ? Object.freeze(result) : []
      return
    }
    _.forEach(item, policy => {
      const dummyObj = initSdwanPolicyAll()
      dummyObj.sdwan_policy_prf_id = policy.sdwan_policy_prf_id
      dummyObj.sdwan_policy_priority = policy.sdwan_policy_priority
      dummyObj.use = policy.sdwan_policy_use_yn // 미사용 항목에 대한 테이블 GUI 회색(비활성화) 처리를 위해 동일한 정보를 use, sdwan_policy_use_yn 으로 둘다 추가
      dummyObj.sdwan_policy_use_yn = policy.sdwan_policy_use_yn
      dummyObj.sdwan_policy_id = policy.sdwan_policy_id
      dummyObj.sdwan_policy_name = policy.sdwan_policy_name
      dummyObj.sdwan_policy_load_balance = policy.sdwan_policy_load_balance
      dummyObj.sdwan_policy_fail_action = policy.sdwan_policy_fail_action
      dummyObj.sdwan_policy_log_yn = policy.sdwan_policy_log_yn
      dummyObj.sdwan_policy_desc = policy.sdwan_policy_desc

      let isEmptyAppObj = false
      _.forEach(['app_obj', 'src_addr_obj', 'dst_addr_obj', 'srv_obj', 'sdwan_path_label_obj'], key => {
        const objArr = _.get(policy, [key], [])
        if (_.isArray(objArr) && objArr.length > 0) dummyObj[key] = objArr
        else if (key === 'app_obj') {
          dummyObj.app_obj = initSdwanApplicationData()
          isEmptyAppObj = true
        } else if (key === 'sdwan_path_label_obj') dummyObj[key] = []
        else if (key === 'srv_obj') dummyObj[key] = initSdwanServiceData()
        else dummyObj[key] = initSdwanAddressData()
      })

      if (_.size(policy.app_filter_obj) > 0) {
        dummyObj.app_filter_obj = policy.app_filter_obj
        // SD-WAN 정책에서는 app_obj 와 app_filter_obj 정보를 합친다.
        let convertedAppFilterObj = null
        if (isEmptyAppObj) dummyObj.app_obj = []
        policy.app_filter_obj.forEach((appFilterObj) => {
          convertedAppFilterObj = {
            app_obj_id: appFilterObj.app_filter_obj_id,
            app_obj_name: appFilterObj.app_filter_obj_uname,
            app_type: 3, // App Filter
            app_filter_obj_desc: appFilterObj.app_filter_obj_desc,
            app_cate: (_.isUndefined(appFilterObj.app_cate) ? [] : appFilterObj.app_cate),
            app_tech: (_.isUndefined(appFilterObj.app_tech) ? [] : appFilterObj.app_tech),
            app_char: (_.isUndefined(appFilterObj.app_char) ? [] : appFilterObj.app_char),
            app_tag: (_.isUndefined(appFilterObj.app_tag) ? [] : appFilterObj.app_tag),
            app_cntry: (_.isUndefined(appFilterObj.app_cntry) ? [] : appFilterObj.app_cntry),
            app_risk: (_.isUndefined(appFilterObj.app_risk) ? [] : appFilterObj.app_risk)
          }
          dummyObj.app_obj.push(convertedAppFilterObj)
        })
      } else {
        dummyObj.app_filter_obj = initSdwanAppFilterData()
      }
      result.push(_.cloneDeep(dummyObj))
    })
    state.sdwanProfilePolicyList = (result.length > 0) ? Object.freeze(result) : []
  },
  /* SD-WAN 정책 조회 적용 */
  getSdwanPolicyList (state, {item, isAddProfilePolicyFlag}) {
    const result = []
    let isFindSameId = false
    if (_.isPlainObject(item)) item = [item]
    if (_.get(item, ['length']) === 0) {
      state.sdwanPolicyList = (result.length > 0) ? Object.freeze(result) : []
      return
    }
    _.forEach(item, policy => {
      // 특정 정책 내 프로파일로 기 등록된 "정책"은 제외시킨다. (프로파일 기능에서 사용)
      if (isAddProfilePolicyFlag && state.sdwanProfilePolicyList.length > 0) {
        isFindSameId = isExistPolicy(state.sdwanProfilePolicyList, policy)
        if (isFindSameId) return // 특정 프로파일에 기 등록된 정책인가?
      }
      const dummyObj = genPolicyObj({isAddProfilePolicyFlag, policy})
      result.push(_.cloneDeep(dummyObj))
    })
    state.sdwanPolicyList = (result.length > 0) ? Object.freeze(result) : []
  },

  // 정책 팝업 창에서 객체 별 추가
  saveSdwanObjectData (state, {item, objectType}) {
    if (_.get(item, ['length']) === 0) {
      if (['srv', 'srv_obj'].includes(objectType)) {
        state.sdwanPolicyData.srv_obj = initSdwanServiceData()
      } else if (['src', 'src_addr_obj'].includes(objectType)) {
        state.sdwanPolicyData.src_addr_obj = initSdwanAddressData()
      } else if (['dst', 'dst_addr_obj'].includes(objectType)) {
        state.sdwanPolicyData.dst_addr_obj = initSdwanAddressData()
      } else if (['app', 'app_obj'].includes(objectType)) {
        state.sdwanPolicyData.app_obj = initSdwanApplicationData()
      } else if (['path', 'path_label_obj'].includes(objectType)) {
        state.sdwanPolicyData.sdwan_path_label_obj = initSdwanPathLabelData()
      }
      state.selectedAddObjectData = void 0
      return
    }
    const totalParam = []
    for (let i = 0; i < item.length; i++) {
      let name = null
      if (['srv', 'srv_obj'].includes(objectType)) {
        name = item[i].srv_obj_uname
      } else if (['src', 'dst', 'src_addr_obj', 'dst_addr_obj'].includes(objectType)) {
        name = item[i].addr_obj_uname
      } else if (['app', 'app_obj'].includes(objectType)) {
        if (!_.isUndefined(item[i].app_type)) item[i].app_obj_type = item[i].app_type
        name = item[i].app_obj_name
      } else if (['path', 'path_label_obj'].includes(objectType)) {
        name = item[i].sdwan_path_label_name
      }
      item[i].name = name
      totalParam.push(item[i])
    }
    if (['srv', 'srv_obj'].includes(objectType)) {
      state.sdwanPolicyData.srv_obj = []
      state.sdwanPolicyData.srv_obj = totalParam
    } else if (['src', 'src_addr_obj'].includes(objectType)) {
      state.sdwanPolicyData.src_addr_obj = []
      state.sdwanPolicyData.src_addr_obj = totalParam
    } else if (['dst', 'dst_addr_obj'].includes(objectType)) {
      state.sdwanPolicyData.dst_addr_obj = []
      state.sdwanPolicyData.dst_addr_obj = totalParam
    } else if (['app', 'app_obj'].includes(objectType)) {
      state.sdwanPolicyData.app_obj = []
      state.sdwanPolicyData.app_obj = totalParam
    } else if (['path', 'path_label_obj'].includes(objectType)) {
      state.sdwanPolicyData.sdwan_path_label_obj = totalParam
    }
    state.selectedAddObjectData = void 0
  },

  // SD-WAN 검색 결과 정책 선택
  searchSelectSdwanPolicyRules (state, data) {
    state.searchSelectedPolicyData = data
  },

  // SD-WAN 검색 결과 정책 선택 초기화
  resetSearchSelectSdwanPolicyRules (state) {
    state.searchSelectedPolicyData = void 0
  },

  // 추가된 객체 선택
  selectedAddObject (state, data) {
    state.selectedAddObjectData = data
  },

  // SD-WAN 정책 선택
  selectedPolicy (state, data) {
    if (_.isPlainObject(data)) {
      state.sdwanPolicyData = data
    } else {
      state.sdwanPolicyData = initSdwanPolicyAll()
    }
  },

  // ===================== 프로파일 전용 기능 (전체 선택 or 전체 해제)
  selectAllProfilePolicy (state, isSelectFlag) {
    if (state.sdwanPolicyList.length <= 0) {
      return
    }

    state.sdwanPolicyList.forEach((policyInfo) => {
      if (!_.isUndefined(policyInfo.sdwan_policy_select_yn)) {
        policyInfo.sdwan_policy_select_yn = (isSelectFlag ? 1 : 0)
      }
    })
  }
}

export const actions = {
  /* SD-WAN 정책 적용/취소 */
  async finalCommand ({state}, type) {
    let res = {code: 'ok'}
    const apiUri = `${poApiPrefix}/sdwan/policy/${type}`,
      axiosConfig = (type === 'apply') ? {applyMsg: true} : {cancelMsg: true}
    try {
      const result = await this.$axios.$put(apiUri, void 0, axiosConfig)
      res = result
    } catch (e) {
      res = {code: 'nok', result: {msg: e}}
    }
    return res
  },

  /* SD-WAN 정책 전체목록 조회 */
  async getPolicyList ({commit}, {isAddProfilePolicyFlag = false, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${poApiPrefix}/sdwan/policy`, {loading})
      if (code === 'ok') {
        commit('getSdwanPolicyList', {item: result, isAddProfilePolicyFlag: isAddProfilePolicyFlag})
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  // SD-WAN 정책 추가 (입력 파라미터가 없고, 추가 팝업을 열 때 호출함)
  async addPolicy () {
    const data = await this.$axios.$post(`${poApiPrefix}/sdwan/policy`, {}, {loading: false, successAlert: false}) // TAMS
    return data
  },

  // SD-WAN 정책 수정
  async updatePolicy ({commit, state, dispatch}, {param, succflag}) {
    const sendParam = {}
    let policyName = param.sdwan_policy_name
    if (policyName === undefined) {
      policyName = ''
    } else {
      policyName = param.sdwan_policy_name
    }
    sendParam.sdwan_policy_id = param.sdwan_policy_id
    sendParam.sdwan_policy_name = policyName
    sendParam.sdwan_policy_load_balance = param.sdwan_policy_load_balance
    sendParam.sdwan_policy_fail_action = param.sdwan_policy_fail_action
    sendParam.sdwan_policy_log_yn = param.sdwan_policy_log_yn
    sendParam.sdwan_policy_desc = param.sdwan_policy_desc

    const data = await this.$axios.$put(`${poApiPrefix}/sdwan/policy/${param.sdwan_policy_id}`, param, {loading: false, loadingShow: false, successAlert: succflag})
    return data
  },

  // SD-WAN 정책 삭제
  async deletePolicy ({state, dispatch}, param) {
    const data = await this.$axios.$delete(`${poApiPrefix}/sdwan/policy`, {
      data: param,
      loading: true,
      successAlert: false
    })
    return data
  },

  async addSdwanObject ({state, dispatch}, { appResult, item, objectType }) {
    const params = {}
    const paramIdList = []
    let res = {}, url = ''
    let objectList = []

    params.sdwan_policy_id = state.sdwanPolicyData.sdwan_policy_id

    if (objectType.indexOf('srv') !== -1) {
      url = `${poApiPrefix}/sdwan/srv_obj`
      params.srv_obj_id_list = item.srv_obj_id_list
    } else if (objectType.indexOf('src') !== -1 || objectType.indexOf('dst') !== -1) {
      url = `${poApiPrefix}/sdwan/addr_obj`
      params.addr_type = (objectType.indexOf('dst') !== -1 ? 2 : 1) // SD-WAN 정책 주소 객체 유형 (1: 출발지, 2: 목적지)
      params.addr_obj_id_list = item.addr_obj_id_list
      if (_.isArray(item.addr_obj_except_yn_list)) {
        params.addr_obj_except_yn_list = item.addr_obj_except_yn_list
      } else {
        if (_.isUndefined(item.addr_obj_except_yn)) {
          params.addr_obj_except_yn_list = [0]
        } else {
          params.addr_obj_except_yn_list = [item.addr_obj_except_yn]
        }
      }
    } else if (objectType.indexOf('app') !== -1) {
      if (item.app_obj_type === 3) {
        objectList = pickAppData(appResult)
      } else {
        objectList = []
        item.app_obj_id_list.forEach((appObjId) => {
          if (_.get(item, ['app_filter_obj_id'])) {
            item.app_obj_id = item.app_filter_obj_id
          }
          objectList.push(pickAppData({app_obj_id: appObjId}))
          // objectList.push({app_obj_id: appObjId})
        })
      }

      if (!_.isArray(objectList) || objectList.length <= 0) {
        res.code = 'nok'
        res.result = {
          msg: 'msg.existObject'
        }
        return res
      }
      objectList.forEach((obj) => {
        paramIdList.push(obj.app_obj_id)
      })
      // params.app_type = item.app_type // 22.12.05 API 설계 변경사항 적용 (app_type 입력 파라미터 삭제. by 이준세 프로)
      params.app_obj_id_list = paramIdList
      url = `${poApiPrefix}/sdwan/app_obj`
    } else if (objectType.indexOf('path') !== -1) {
      params.sdwan_path_label_id_list = item.sdwan_path_label_id_list
      url = `${poApiPrefix}/sdwan/path_label_obj`
    }

    res = await this.$axios.$post(url, params, {
      loading: false,
      successAlert: false
    })

    return res
  },

  // SD-WAN 정책 내 객체 삭제 (단일)
  async deleteSdwanObject ({state, dispatch}, {item, objectType} = {}) {
    let url = '', res = {}
    const params = {
      sdwan_policy_id: state.sdwanPolicyData.sdwan_policy_id
    }

    if (objectType.indexOf('srv') !== -1) {
      params.srv_obj_id_list = item.srv_obj_id_list
      url = `${poApiPrefix}/sdwan/srv_obj`
      res = await this.$axios.$delete(url, {
        data: params,
        loading: true,
        successAlert: false
      })
    } else if (objectType.indexOf('src') !== -1 || objectType.indexOf('dst') !== -1) {
      params.addr_type = (objectType === 'src' ? 1 : 2) // SD-WAN 정책 주소 객체 유형 (1: 출발지, 2: 목적지)
      params.addr_obj_id_list = item.addr_obj_id_list
      url = `${poApiPrefix}/sdwan/addr_obj`
      res = await this.$axios.$delete(url, {
        data: params,
        loading: true,
        successAlert: false
      })
    } else if (objectType.indexOf('app') !== -1) {
      url = `${poApiPrefix}/sdwan/app_obj`
      params.app_obj_id_list = item.app_obj_id_list
      // params.app_type = item.app_type // 22.12.05 API 설계 변경사항 적용 (app_type 입력 파라미터 삭제. by 이준세 프로)
      res = await this.$axios.$delete(url, {
        data: params,
        loading: true,
        successAlert: false
      })
    } else if (objectType.indexOf('path') !== -1) {
      url = `${poApiPrefix}/sdwan/path_label_obj`
      params.sdwan_path_label_id_list = item.sdwan_path_label_id_list
      res = await this.$axios.$delete(url, {
        data: params,
        loading: true,
        successAlert: false
      })
    }
    return res
  },

  async changeExceptObject ({state, dispatch}, item) {
    const params = {
      sdwan_policy_id: state.sdwanPolicyData.sdwan_policy_id,
      addr_type: item.addr_obj_type,
      addr_obj_id: item.addr_obj_id,
      addr_obj_except_yn: item.addr_obj_except_yn
    }
    const result = await this.$axios.$put(`${poApiPrefix}/sdwan/addr_obj`, params, {loading: false, successAlert: false})
    return result
  },

  async getSdwanObject ({state, commit, dispatch}, { item, objectType }) {
    let res = void 0, url = ''
    const sdwanPolicyId = state.sdwanPolicyData.sdwan_policy_id

    if (objectType.indexOf('srv') !== -1) {
      url = `${poApiPrefix}/sdwan/srv_obj/${sdwanPolicyId}`
    } else if (objectType.indexOf('src') !== -1 || objectType.indexOf('dst') !== -1) {
      const sdwanPolicyAddrObjType = objectType.indexOf('dst') !== -1 ? 2 : 1
      url = `${poApiPrefix}/sdwan/addr_obj/${sdwanPolicyId}/${sdwanPolicyAddrObjType}`
    } else if (objectType.indexOf('app') !== -1) {
      url = `${poApiPrefix}/sdwan/app_obj/${sdwanPolicyId}`
    } else if (objectType.indexOf('path') !== -1) {
      url = `${poApiPrefix}/sdwan/path_label_obj/${sdwanPolicyId}`
    }

    res = await this.$axios.$get(url, {
      loading: false,
      successAlert: false
    })

    if (res.code === 'ok') {
      commit('saveSdwanObjectData', {
        item: res.result,
        objectType
      })
    }

    return res
  },

  // ================== 프로파일 전용 기능
  /* 특정 프로파일에 대한 정책 조회 */
  async getProfilePolicyList ({commit}, {param, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${poApiPrefix}/sdwan/policy/profile/${param.sdwan_policy_prf_id}`, {loading})
      if (code === 'ok') {
        commit('getSdwanProfileList', {item: result})
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 특정 프로파일에 대한 정책 추가 */
  async addProfilePolicy ({commit, state}, {param, loading = true, successAlert = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$post(`${poApiPrefix}/sdwan/policy/profile`,
        param, {loading: loading, successAlert: successAlert})
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 특정 프로파일에 대한 정책 삭제 */
  async deleteProfilePolicy ({state}, {param, successAlert = false}) {
    let res = {code: 'ok'}
    try {
      const {
        code,
        result
      } = await this.$axios.$delete(`${poApiPrefix}/sdwan/policy/profile`, {
        data: param,
        successAlert: successAlert,
        loading: true
      })
      if (code !== 'ok') {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 특정 프로파일에 대한 정책 이동 */
  async moveProfilePolicy ({state}, {param, loading = true, successAlert = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {
        code, result
      } = await this.$axios.$put(`${poApiPrefix}/sdwan/policy/profile/priority`, param, {loading, successAlert})
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 특정 프로파일에 대한 정책 사용 여부 */
  async useProfilePolicy ({state}, {param, loading = true, successAlert = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {
        code, result
      } = await this.$axios.$put(`${poApiPrefix}/sdwan/policy/profile/use`, param, {loading, successAlert})
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  }
}

export const getters = {}

export const strict = false
